import { useEffect, useRef } from "react";
import introJs from "intro.js";
import "intro.js/introjs.css";

interface OnboardingTourProps {
  onComplete?: () => void;
  autoStart?: boolean;
}

export function OnboardingTour({ onComplete, autoStart = false }: OnboardingTourProps) {
  const introRef = useRef<ReturnType<typeof introJs> | null>(null);

  useEffect(() => {
    // Initialize intro.js
    introRef.current = introJs();

    // Configure the tour steps
    introRef.current.setOptions({
      steps: [
        {
          title: "Welcome to StreamPilot! 🎉",
          intro: "Let's take a quick tour to help you get started with managing your live streams.",
        },
        {
          element: '[data-testid="stat-active-streams"]',
          title: "Active Streams",
          intro: "Monitor your currently live streams at a glance. This card shows how many streams are active and on which platforms.",
          position: "bottom",
        },
        {
          element: '[data-testid="stat-platforms-connected"]',
          title: "Connected Platforms",
          intro: "See how many streaming platforms you have configured. StreamPilot supports YouTube, Facebook, X (Twitter), and Twitch.",
          position: "bottom",
        },
        {
          element: '[data-testid="stat-total-streams"]',
          title: "Stream History",
          intro: "Track your total number of streams across all time.",
          position: "bottom",
        },
        {
          element: '[data-testid="stat-next-service"]',
          title: "Next Scheduled Stream",
          intro: "View your upcoming scheduled streams so you never miss a broadcast.",
          position: "bottom",
        },
        {
          element: '[data-tour="create-stream-button"]',
          title: "Create a New Stream 🎬",
          intro: "Click here to create and schedule a new stream. You can configure the title, speaker, platforms, and scheduling details.",
          position: "bottom",
        },
        {
          element: '[data-tour="platforms-section"]',
          title: "Platform Management",
          intro: "This section shows your connected streaming platforms and their current status. Each platform can be configured independently.",
          position: "top",
        },
        {
          element: '[data-tour="speakers-nav"]',
          title: "Manage Speakers 🎤",
          intro: "Add and manage your speakers (pastors, presenters, etc.) here. Each stream can be assigned to a specific speaker.",
          position: "bottom",
        },
        {
          element: '[data-tour="platforms-nav"]',
          title: "Platform Connections 🔗",
          intro: "Connect your streaming accounts for YouTube, Facebook, X, and Twitch. Once connected, you can stream to multiple platforms simultaneously.",
          position: "bottom",
        },
        {
          element: '[data-tour="settings-nav"]',
          title: "Settings ⚙️",
          intro: "Manage your organization settings, team members, and preferences. You can also restart this tour anytime from the settings page.",
          position: "bottom",
        },
        {
          title: "You're All Set! 🚀",
          intro: "That's it! You're ready to start streaming. If you need help, you can restart this tour from the Settings page. Happy streaming!",
        },
      ],
      exitOnOverlayClick: false,
      showStepNumbers: true,
      showBullets: true,
      showProgress: true,
      scrollToElement: true,
      tooltipClass: "customIntroTooltip",
      highlightClass: "customIntroHighlight",
    });

    // Handle tour completion
    introRef.current.oncomplete(() => {
      localStorage.setItem("streampilot-tour-completed", "true");
      if (onComplete) {
        onComplete();
      }
    });

    // Handle tour exit
    introRef.current.onexit(() => {
      localStorage.setItem("streampilot-tour-completed", "true");
      if (onComplete) {
        onComplete();
      }
    });

    // Auto-start if requested
    if (autoStart) {
      // Small delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        introRef.current?.start();
      }, 500);

      return () => clearTimeout(timer);
    }

    return () => {
      introRef.current?.exit();
    };
  }, [autoStart, onComplete]);

  return null;
}

export function startTour() {
  localStorage.removeItem("streampilot-tour-completed");
  window.location.reload(); // Reload to trigger auto-start
}

export function shouldShowTour(): boolean {
  return localStorage.getItem("streampilot-tour-completed") !== "true";
}

export function resetTour() {
  localStorage.removeItem("streampilot-tour-completed");
}
